import React, { useState } from 'react';
import { ResponsiveContainer, PieChart as RePie, Pie, Cell, Tooltip as RechartsTooltip } from 'recharts';
import { Wallet, List, CheckCircle, CheckSquare, Square, Pencil, Trash2, X, CreditCard as CardIcon, Cloud, FileText, Settings, ChevronLeft, ChevronRight, AlertTriangle, RefreshCw, History, TrendingUp, DollarSign } from 'lucide-react';
import { Card } from '../ui/Card';
import { MasterTipsCard } from '../ui/MasterTipsCard';
import { CloseOutModal } from '../tracker/CloseOutModal';
import { useTrackerPeriod } from '../../hooks/useTrackerPeriod';
import { generateCalendarDays, formatDate } from '../../utils/tracker';
import { formatCurrency } from '../../utils/finance';
import { CHART_COLORS } from '../../utils/constants';

// --- SETTINGS MODAL (Unchanged) ---
const SettingsModal = ({ onClose, data, actions }) => {
    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-slate-900 border border-slate-700 p-6 rounded-xl w-full max-w-md shadow-2xl">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Settings className="w-5 h-5 text-orange-500" /> Tracker Settings</h3>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs text-slate-400 uppercase font-bold block mb-2">Pay Schedule Frequency</label>
                        <select value={data.trackerPayFreq || 'bi-weekly'} onChange={(e) => actions.update('trackerPayFreq', e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded p-2 text-white outline-none focus:border-orange-500">
                            <option value="bi-weekly">Bi-Weekly (Every 14 Days)</option>
                            <option value="monthly">Monthly (Every 30 Days)</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-400 uppercase font-bold block mb-2">Anchor Pay Date (Start)</label>
                        <input type="date" value={data.trackerPeriodStart} onChange={(e) => actions.update('trackerPeriodStart', e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded p-2 text-white outline-none focus:border-orange-500" />
                        <p className="text-[10px] text-slate-500 mt-1">Pick a recent payday. All periods calculate from here.</p>
                    </div>
                </div>
                <div className="mt-6 flex justify-end">
                    <button onClick={onClose} className="bg-orange-500 text-slate-900 font-bold px-4 py-2 rounded hover:bg-orange-600">Done</button>
                </div>
            </div>
        </div>
    );
};

export const TrackerTab = ({ data, actions, calculations, theme }) => {
    const { trackerData } = calculations;
    const { trackerOffset } = actions; 
    
    // --- UI STATE ---
    const [showSettings, setShowSettings] = useState(false);
    const [newCategory, setNewCategory] = useState("");
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [showImport, setShowImport] = useState(false);
    
    // H10 Quick Add State
    const [quickAmount, setQuickAmount] = useState('');
    const [quickDescription, setQuickDescription] = useState('');

    // --- LOGIC & STATE DELEGATION (H12) ---
    const period = useTrackerPeriod(data, actions, trackerData, trackerOffset, theme);
    const { handlePeriodClose, isBillPaid, toggleBillPaid, handleCreditSweep } = period;

    // --- CALENDAR ---
    const calendarDays = generateCalendarDays(new Date(trackerData.start));
    
    const isPayday = (date) => {
        const startAnchor = new Date(data.trackerPeriodStart);
        const diffTime = date.getTime() - startAnchor.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (data.trackerPayFreq === 'monthly') return date.getDate() === startAnchor.getDate();
        return diffDays % 14 === 0;
    };

    const daysLeft = Math.max(0, Math.ceil((new Date(trackerData.end) - new Date()) / (1000 * 3600 * 24)));

    // --- HELPER: Get Intelligent Date for Entry ---
    const getTransactionDateForView = () => {
        const now = new Date();
        const start = new Date(trackerData.start);
        const end = new Date(trackerData.end);
        
        // If today is within the viewed period, use real time for accuracy
        if (now >= start && now <= end) return now.toISOString();
        
        // Otherwise, default to the start of the period so it appears in this view
        return start.toISOString();
    };

    // --- QUICK ADD HANDLER (H10 Fixed) ---
    const handleQuickAddInflow = () => {
        const amount = parseFloat(quickAmount);
        if (amount > 0) {
            const date = getTransactionDateForView();
            // desc, amount, isCredit, isIncome, category, targetDate
            actions.recordQuickTransaction(quickDescription, amount, false, true, "Income", date);
            setQuickAmount('');
            setQuickDescription('');
            alert(`+${formatCurrency(amount)} added to this period's budget!`);
        }
    };

    // --- RENDER ---
    return (
        <div className="animate-fade-in pb-10">
            {showSettings && <SettingsModal onClose={() => setShowSettings(false)} data={data} actions={actions} />}
            
            <MasterTipsCard 
                title="Master Your Variable Spend" 
                subtitle="Control the burn. Track every spark." 
                element="fire"
            >
                <ul className="list-disc pl-4 space-y-1 mt-1">
                    <li><strong>Step 1:</strong> List all fixed bills in the <strong>Burn (Fire)</strong> tab first.</li>
                    <li><strong>Step 2:</strong> Your budget here is automatically calculated as <strong>(Income - Fixed Bills)</strong>.</li>
                    <li><strong>Don't know your budget?</strong> Just track against the calculated "Safe to Spend" number. Watch for patterns, then refine your spending to unlock more surplus for Debt & Savings.</li>
                </ul>
            </MasterTipsCard>

            {/* ALERTS */}
            {trackerData.isDirty && (
                <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/50 rounded-xl flex items-center justify-between gap-4 animate-slide-up">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-500/20 rounded-full"><RefreshCw className="w-5 h-5 text-yellow-500 animate-spin-slow"/></div>
                        <div>
                            <h4 className="text-sm font-bold text-yellow-100">History Updated</h4>
                            <p className="text-xs text-yellow-200/70">You changed expenses in a closed period. Re-log to update your carryover.</p>
                        </div>
                    </div>
                    <button onClick={handlePeriodClose} className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 font-bold px-4 py-2 rounded text-xs whitespace-nowrap shadow-lg">
                        Update & Re-Log
                    </button>
                </div>
            )}

            {trackerData.gapDetected && (
                <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/50 rounded-xl flex items-center gap-4 animate-slide-up">
                    <div className="p-2 bg-blue-500/20 rounded-full"><History className="w-5 h-5 text-blue-400"/></div>
                    <div>
                        <h4 className="text-sm font-bold text-blue-100">Welcome Back (Gap Detected)</h4>
                        <p className="text-xs text-blue-200/70">
                            It looks like you missed a tracking period since {formatDate(trackerData.targetDate)}. 
                            No worries! We've started a fresh sheet. If you want to carry over debt/surplus from the gap, use the <strong>Override</strong> box below.
                        </p>
                    </div>
                </div>
            )}

            {trackerData.unpaidCreditWarning && (
                <div className="bg-red-500/10 border border-red-500/50 p-3 rounded-lg flex flex-col sm:flex-row items-center gap-3 mb-6">
                     <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                     <div className="flex-1 text-center sm:text-left">
                         <p className="text-sm font-bold text-red-400">Unpaid Credit Transactions</p>
                         <p className="text-xs text-slate-400">You have unchecked credit expenses in previous periods.</p>
                     </div>
                     <div className="flex gap-2">
                        <button onClick={() => actions.setTrackerOffset(trackerOffset - 1)} className="text-xs bg-red-500/20 text-red-300 px-3 py-1 rounded hover:bg-red-500/30 whitespace-nowrap">Review Past</button>
                        <button onClick={handleCreditSweep} className="text-xs bg-red-500 hover:bg-red-400 text-white font-bold px-3 py-1 rounded whitespace-nowrap flex items-center gap-1 shadow-lg"><CheckCircle className="w-3 h-3"/> Pay Statement</button>
                     </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
                {/* 1. CALENDAR */}
                <Card element="fire" className="lg:col-span-1 flex flex-col">
                    <div className="flex justify-between items-center mb-2 text-xs">
                        <button onClick={() => actions.setTrackerOffset(trackerOffset - 1)} className="hover:text-white text-slate-400"><ChevronLeft className="w-4 h-4" /></button>
                        <span className="font-bold text-white">{new Date(trackerData.start).toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
                        <button onClick={() => actions.setTrackerOffset(trackerOffset + 1)} className="hover:text-white text-slate-400"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 flex-1">
                        {['S','M','T','W','T','F','S'].map(d => <div key={d} className="text-[10px] text-center text-slate-500 font-bold">{d}</div>)}
                        {calendarDays.map((day, i) => {
                            const tStart = new Date(trackerData.start); tStart.setHours(0,0,0,0);
                            const tEnd = new Date(trackerData.end); tEnd.setHours(0,0,0,0);
                            const dTime = day.setHours(0,0,0,0);
                            
                            const inPeriod = dTime >= tStart && dTime <= tEnd;
                            const isCurrent = new Date().setHours(0,0,0,0) === dTime;
                            const payday = isPayday(new Date(day));

                            return (
                                <div key={i} className={`h-6 flex items-center justify-center rounded text-[10px] relative ${inPeriod ? 'bg-slate-800 text-white' : 'text-slate-600'} ${isCurrent ? 'border border-orange-500 font-bold text-orange-400' : ''}`}>
                                    {day.getDate()}
                                    {payday && <div className="absolute bottom-0.5 w-1 h-1 bg-emerald-500 rounded-full"></div>}
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-2 text-center">
                        <span className="text-xs text-slate-500">{daysLeft} days remaining</span>
                        <button onClick={() => setShowSettings(true)} className="ml-2 text-[10px] text-orange-400 underline decoration-dotted">Settings</button>
                    </div>
                </Card>

                {/* 2. BUDGET STATUS */}
                <Card element="fire" className="bg-orange-900/10 border-orange-500/20 lg:col-span-2 flex flex-col justify-center">
                    <div className="flex justify-between items-start mb-2">
                        <div>
                            <h3 className="font-bold text-orange-200 flex items-center gap-2 text-xl"><Wallet className="w-6 h-6"/> Period Budget</h3>
                            <p className="text-sm text-orange-400 font-bold mt-1">
                                {new Date(trackerData.start).toLocaleDateString(undefined, {month:'short', day:'numeric'})} - {new Date(trackerData.end).toLocaleDateString(undefined, {month:'short', day:'numeric'})}
                            </p>
                            
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-300">
                                <span>{formatCurrency(trackerData.periodIncome)} Income</span>
                                <span className="text-slate-500">-</span>
                                <span>{formatCurrency(trackerData.expectedSum)} Bills</span>
                                <span className="text-slate-500">=</span>
                                <span className="font-bold text-orange-200">Safe to Spend</span>
                            </div>

                            <div className="text-xs text-slate-400 mt-1">
                                {trackerData.pendingAdjustmentApplied !== 0 && (
                                    <span className={trackerData.pendingAdjustmentApplied > 0 ? "text-red-400 ml-1" : "text-emerald-400 ml-1"}>
                                        ({trackerData.pendingAdjustmentApplied > 0 ? '-' : '+'}{formatCurrency(Math.abs(trackerData.pendingAdjustmentApplied))} Carryover)
                                    </span>
                                )}
                                {/* H10: Show Quick Inflow Adjustment */}
                                {trackerData.periodInflows > 0 && (
                                    <span className="text-emerald-400 ml-1">
                                        (+{formatCurrency(trackerData.periodInflows)} Quick Inflow)
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="text-xs text-orange-400/70 block">Remaining</span>
                            <span className={`text-4xl font-bold ${trackerData.remaining < 0 ? 'text-red-500 animate-pulse' : 'text-white'}`}>{formatCurrency(trackerData.remaining)}</span>
                        </div>
                    </div>
                    <div className="w-full bg-slate-900 rounded-full h-4 overflow-hidden mb-2 relative">
                        <div className={`h-full transition-all duration-500 ${trackerData.remaining < 0 ? 'bg-red-500' : 'bg-gradient-to-r from-orange-600 to-orange-400'}`} style={{width: `${Math.min(100, Math.max(0, (trackerData.spent / trackerData.suggestedBudget) * 100))}%`}}></div>
                    </div>
                    <div className="flex items-center gap-2 justify-between text-xs">
                        <div className="text-slate-500">Target: {formatCurrency(trackerData.suggestedBudget)}</div>
                        <div className="flex items-center gap-1 group relative">
                            <span className="text-slate-500">Override:</span>
                            <input type="number" value={data.discretionaryBudget} onChange={(e) => actions.update('discretionaryBudget', parseFloat(e.target.value))} className="bg-slate-900 border border-slate-700 rounded px-2 py-0.5 w-16 text-white focus:border-orange-500 outline-none" placeholder="Auto" />
                            <div className="absolute bottom-full right-0 mb-2 w-56 bg-slate-800 p-3 rounded shadow-lg border border-slate-700 hidden group-hover:block text-[10px] text-slate-300 z-50">
                                Don't know your number? Leave this blank and track against your calculated "Safe to Spend" first. You can set a strict limit later once you know your baseline.
                            </div>
                        </div>
                    </div>
                </Card>

                {/* 3. PIE CHART */}
                <Card element="fire" className="lg:col-span-1 flex flex-col items-center justify-center p-2">
                     <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Spending Breakdown</h4>
                     <div className="w-full h-32 relative group">
                        <ResponsiveContainer width="100%" height="100%">
                             <RePie>
                                 <Pie data={trackerData.pieData} innerRadius={35} outerRadius={60} paddingAngle={2} dataKey="value">
                                     {trackerData.pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={CHART_COLORS.fire[index % CHART_COLORS.fire.length]} stroke="#1e293b" />)}
                                 </Pie>
                                 <RechartsTooltip contentStyle={{backgroundColor: '#0f172a', borderColor: theme.hex, color: '#fff', fontSize:'10px'}} formatter={v => formatCurrency(v)} itemStyle={{color: '#fff'}} />
                             </RePie>
                        </ResponsiveContainer>
                        {trackerData.pieData.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-600">No Data</div>}
                     </div>
                </Card>
            </div>

            {/* H10: QUICK ADD INFLOW WIDGET */}
            <div className="mb-6">
                <Card element="water" className="bg-blue-900/10 border-blue-500/20">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-blue-400"/> Quick Inflow</h3>
                        <p className="text-xs text-slate-400">Add unexpected income to budget.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                            <input
                                type="number"
                                step="0.01"
                                value={quickAmount}
                                onChange={(e) => setQuickAmount(e.target.value)}
                                placeholder="Amount"
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg py-2 pl-10 pr-3 text-white outline-none focus:border-blue-500"
                            />
                        </div>
                        <input
                            type="text"
                            value={quickDescription}
                            onChange={(e) => setQuickDescription(e.target.value)}
                            placeholder="Description (Optional)"
                            className="w-full sm:flex-1 bg-slate-950 border border-slate-700 rounded-lg py-2 px-3 text-white outline-none focus:border-blue-500"
                        />
                        <button
                            onClick={handleQuickAddInflow}
                            disabled={!parseFloat(quickAmount) || parseFloat(quickAmount) <= 0}
                            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-bold px-6 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            <TrendingUp className="w-4 h-4" /> Add to Budget
                        </button>
                    </div>
                </Card>
            </div>

            {/* LISTS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card element="fire">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-white flex items-center gap-2"><FileText className="w-5 h-5 text-orange-400"/> Expected Kindling</h3>
                        <span className="text-xs text-slate-400">{formatCurrency(trackerData.expectedSum)}</span>
                    </div>
                    <div className="space-y-2 h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-800 pr-1">
                        {trackerData.expected.map(ex => {
                             const paid = isBillPaid(ex.id);
                             return (
                                <div key={ex.id} className={`flex justify-between items-center text-sm p-3 rounded border transition-colors ${paid ? 'bg-emerald-900/10 border-emerald-500/30 opacity-60' : 'bg-slate-950/50 border-slate-800/50'}`}>
                                    <div>
                                        <span className={`block font-medium ${paid ? 'text-emerald-400 line-through' : 'text-slate-200'}`}>{ex.name}</span>
                                        <span className="text-slate-500 text-xs">Due Day {ex.dayDue}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-orange-300 font-mono">{formatCurrency(ex.amount)}</span>
                                        <button onClick={() => toggleBillPaid(ex.id)} className={`p-1 rounded ${paid ? 'text-emerald-400' : 'text-slate-600 hover:text-white'}`}>
                                            {paid ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                             )
                        })}
                        {trackerData.expected.length === 0 && (
                            <p className="text-xs text-slate-500 italic text-center p-4">
                                No bills scheduled for this period.
                            </p>
                        )}
                    </div>
                </Card>
                 
                <Card element="fire">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-2"><h3 className="font-bold text-white flex items-center gap-2"><List className="w-5 h-5 text-orange-400"/> Discretionary Burn</h3></div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{formatCurrency(trackerData.spent)}</span>
                            <button onClick={() => setShowImport(!showImport)} className="text-xs bg-slate-800 text-orange-400 px-2 py-1 rounded flex items-center gap-1"><Cloud className="w-3 h-3"/> CSV</button>
                        </div>
                    </div>
                    {showImport && (
                        <div className="mb-4 p-4 bg-slate-900 border border-slate-700 border-dashed rounded-lg animate-fade-in">
                            <p className="text-xs text-slate-400 mb-2">Upload CSV...</p>
                            <button onClick={() => setShowImport(false)} className="text-xs text-red-400 mt-2">Cancel</button>
                        </div>
                    )}
                    {/* Manual Transaction Input (H10 Fixed) */}
                    <div className="bg-slate-950 p-2 rounded-lg border border-slate-800 mb-3 grid grid-cols-2 gap-2">
                         <input id="tDesc" placeholder="Item..." className="bg-transparent border-b border-slate-700 text-xs outline-none col-span-2 text-white focus:border-orange-500 p-1" />
                         <input id="tAmt" type="number" placeholder="$0.00" className="bg-transparent border-b border-slate-700 text-xs outline-none text-white focus:border-orange-500 p-1" />
                         <div className="flex gap-1 col-span-2">
                             {isAddingCategory ? (
                                <div className="flex gap-1 w-full"><input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="New Category Name..." className="bg-slate-900 text-xs border border-slate-700 rounded w-full px-2 py-1 text-white outline-none focus:border-orange-500" /><button onClick={() => { if(newCategory) { actions.update('customCategories', [...data.customCategories, newCategory]); setNewCategory(""); setIsAddingCategory(false); }}} className="text-xs bg-orange-500 text-slate-900 px-3 rounded font-bold">Save</button><button onClick={() => setIsAddingCategory(false)} className="text-slate-500"><X className="w-4 h-4"/></button></div>
                             ) : (
                                <div className="flex w-full gap-2">
                                    <select id="tCat" className="bg-slate-900 text-xs border-none rounded text-slate-400 w-full outline-none h-6">{data.customCategories.map(c => <option key={c}>{c}</option>)}</select>
                                    <button onClick={() => setIsAddingCategory(true)} className="bg-slate-800 text-xs text-slate-400 px-2 rounded hover:bg-slate-700 whitespace-nowrap">+ New</button>
                                </div>
                             )}
                         </div>
                         <div className="flex items-center gap-2 col-span-2">
                             <input id="tCredit" type="checkbox" className="rounded border-slate-700 bg-slate-900 accent-orange-500" /> <span className="text-[10px] text-slate-500">Credit?</span>
                             <button onClick={() => {
                                 const desc = document.getElementById('tDesc').value; 
                                 const amt = parseFloat(document.getElementById('tAmt').value); 
                                 const cat = document.getElementById('tCat').value; 
                                 const credit = document.getElementById('tCredit').checked;
                                 
                                 if(desc && amt) { 
                                    const date = getTransactionDateForView();
                                    // desc, amount, isCredit, isIncome, category, targetDate
                                    actions.recordQuickTransaction(desc, amt, credit, false, cat, date);
                                     document.getElementById('tDesc').value = ""; 
                                     document.getElementById('tAmt').value = ""; 
                                 }
                             }} className="bg-orange-500 hover:bg-orange-600 text-slate-900 rounded text-xs font-bold px-3 py-1 ml-auto transition-colors">Add</button>
                         </div>
                    </div>
                    <div className="space-y-1 h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-800 pr-1">
                        {trackerData.actuals.map(t => (
                            <div key={t.id} className="flex justify-between items-center p-2 hover:bg-slate-900 rounded border-b border-slate-800/50">
                                {editingTransaction === t.id ? (
                                    <div className="flex flex-col gap-2 w-full">
                                        <div className="flex gap-2"><input defaultValue={t.desc} className="bg-slate-900 w-full text-xs text-white p-1 border border-slate-700 rounded" onBlur={(e) => actions.updateArrayItem('transactions', t.id, 'desc', e.target.value)} /><input type="number" defaultValue={t.amount} className="bg-slate-900 w-20 text-xs text-white p-1 border border-slate-700 rounded" onBlur={(e) => actions.updateArrayItem('transactions', t.id, 'amount', parseFloat(e.target.value))} /></div>
                                        <div className="flex gap-2 items-center"><button onClick={() => setEditingTransaction(null)} className="text-emerald-400 bg-emerald-900/30 p-1 rounded"><CheckCircle className="w-3 h-3" /></button></div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <div className={`font-medium text-xs ${t.isInflow ? 'text-emerald-400' : 'text-slate-200'}`}>{t.desc}</div>
                                            <div className="text-[10px] text-slate-500 flex items-center gap-1">{formatDate(t.date)} • {t.category} {t.isCredit && <span className="text-orange-400 flex items-center gap-0.5"><CardIcon className="w-3 h-3"/> Credit</span>}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`font-mono text-xs ${t.isInflow ? 'text-emerald-400' : 'text-white'}`}>{t.isInflow ? '+' : ''}{formatCurrency(t.amount)}</span>
                                            {!t.isInflow && t.isCredit && ( <button onClick={() => actions.updateArrayItem('transactions', t.id, 'paidOff', !t.paidOff)} className={`p-1 rounded ${t.paidOff ? 'text-emerald-400' : 'text-slate-600 hover:text-white'}`}>{t.paidOff ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}</button> )}
                                            <button onClick={() => setEditingTransaction(t.id)} className="text-slate-600 hover:text-blue-400"><Pencil className="w-3 h-3" /></button>
                                            <button onClick={() => actions.removeArrayItem('transactions', t.id)} className="text-slate-600 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* BOTTOM ROW: CLOSE OUT MODAL (H12 Refactor) */}
            <CloseOutModal 
                trackerData={trackerData} 
                period={period} 
                data={data} 
            />
        </div>
    );
};